import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';
import AppContext from '../contexts/AppContext';
import withLeftSideBar from '../hocs/withLeftSideBar';
import IconButton from '../components/IconButton';
import Spinner from '../components/Spinner';
import {
  MainHeader,
  MainContentTopBar,
  ContainerWrapper,
} from '../components/basicComponents';
import AiInterpreterTable from '../components/AiInterpreterTable';
import SendTranscriptEmailDialog from '../components/SendTranscriptEmailDialog';
import i18n from '../i18n';
import { getAiSessions, sendTranscriptEmail } from '../services/aiSessionsService';
import { resolveDownloadUrl } from '../services/aiBackendService';
import { showError, showSuccess } from '../services/toastService';

Modal.setAppElement('#main');

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
`;

const downloadErrorMessage = (status) => {
  if (status === 410 || status === 404) {
    return i18n('ai_download_unavailable');
  }
  if (status === 403) {
    return i18n('ai_download_forbidden');
  }
  if (status === 504) {
    return i18n('ai_download_timeout');
  }
  return i18n('requestErrorMessage');
};

const AiInterpreter = ({ toggleSideBar, sidebarDocked, ...rest }) => {
  const { colors, layout } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const { user } = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [mode, setMode] = useState(null);
  const [emailSession, setEmailSession] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const { sessions: list, transcriptionSummaryMode } = await getAiSessions();
        if (!cancelled) {
          setSessions(list);
          setMode(transcriptionSummaryMode);
        }
      } catch (e) {
        if (e.message === 'Session expired') {
          sessionExpired();
        } else {
          showError(i18n('requestErrorMessage'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (resource, session) => {
    try {
      const parts = [];
      if (resource === 'transcript') {
        parts.push('format=txt');
      }
      if (mode) {
        parts.push(`anonymized=${mode === 'anonymized'}`);
      }
      const query = parts.length ? `?${parts.join('&')}` : '';
      const url = await resolveDownloadUrl(resource, session.chatRoomId, query);
      window.open(url, '_blank');
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(downloadErrorMessage(e.status));
      }
    }
  };

  const handleSendEmail = (session) => {
    setEmailSession(session);
  };

  const closeEmailModal = () => {
    if (!sendingEmail) {
      setEmailSession(null);
    }
  };

  const handleSubmitEmail = async (email, includeSummary, emailSubject) => {
    if (!emailSession) {
      return;
    }
    setSendingEmail(true);
    try {
      await sendTranscriptEmail(emailSession.chatRoomId, email, includeSummary, emailSubject);
      showSuccess(i18n('ai_email_sent'));
      setEmailSession(null);
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(i18n('ai_email_failed'));
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const defaultEmail = (user && user.email) || '';

  return (
    <StyledContainer {...rest}>
      <MainContentTopBar borderBottomColor={colors.webDeviderColor}>
        {sidebarDocked ? null : (
          <IconButton
            icon={'menu'}
            iconLib={'material'}
            iconColor={colors.tulkaMainColor}
            onClick={toggleSideBar}
          />
        )}
        <MainHeader
          sidebarDocked={sidebarDocked}
          padding={layout.padding}
          color={colors.webDarkBlue}
        >
          {i18n('ai_interpreter')}
        </MainHeader>
      </MainContentTopBar>
      <ContainerWrapper>
        <AiInterpreterTable
          sessions={sessions}
          onDownload={handleDownload}
          onSendEmail={handleSendEmail}
        />
      </ContainerWrapper>
      <Modal
        isOpen={!!emailSession}
        onRequestClose={closeEmailModal}
        style={layout.modalConfirmationStyles}
      >
        <SendTranscriptEmailDialog
          defaultEmail={defaultEmail}
          allowedDomains={user?.emailSendingDomains}
          sending={sendingEmail}
          onSubmit={handleSubmitEmail}
          onCancel={closeEmailModal}
        />
      </Modal>
      {loading && <Spinner overlay />}
    </StyledContainer>
  );
};

export default withLeftSideBar(AiInterpreter);
